/**
 * Stained-glass colour themes for WpgThumbnail.
 *
 * A WpgTheme defines a background colour plus a small set of filled polygon
 * regions, all in tile-space [0,1]². The thumbnail engine applies every
 * wallpaper-group transform to these regions so the symmetry reads clearly
 * without clutter.
 *
 * WPG_THEMES is the hand-curated stub — paste your favourite designs from the
 * /theme-picker.html dev page here. Any WPG without an entry falls back to a
 * randomly generated theme on first render.
 */
import type { WallpaperGroupId } from './groups';

// ── Type ──────────────────────────────────────────────────────────────────────

export interface WpgTheme {
	/** Hex colour for the tile background. */
	bg: string
	/**
	 * Filled regions in tile-space. Colour is indexed into the 3-colour palette.
	 * Regions are drawn over the bg, then cell outlines are drawn on top.
	 */
	regions: Array<{ pts: [number, number][]; ci: 0 | 1 | 2 }>
	/** 3-colour palette (hex). */
	colors: [string, string, string]
}

// ── Curated themes stub — paste favourites from /theme-picker.html ────────────

export const WPG_THEMES: Partial<Record<WallpaperGroupId, WpgTheme>> = {
	p1: {
		bg: '#2a2437',
		colors: ['#62e9e0', '#e062e9', '#e9e062'],
		regions: [
			{
				pts: [
					[0.1, 0.1],
					[0.5, 0.05],
					[0.6, 0.4],
					[0.35, 0.5],
					[0.15, 0.3],
				],
				ci: 0,
			},
			{
				pts: [
					[0.5, 0.5],
					[0.85, 0.6],
					[0.9, 0.95],
					[0.55, 0.9],
				],
				ci: 1,
			},
		],
	},
	p2: {
		bg: '#241d31',
		colors: ['#27dab1', '#b127da', '#dab127'],
		regions: [
			{
				pts: [
					[0.74, 0.18],
					[0.9, 0.32],
					[0.74, 0.5],
					[0.58, 0.32],
				],
				ci: 2,
			},
			{
				pts: [
					[0.5, 0.692],
					[0.412, 0.8],
					[0.588, 0.8],
				],
				ci: 0,
			},
		],
	},
	pm: {
		bg: '#342031',
		colors: ['#28b1de', '#de28b1', '#b1de28'],
		regions: [
			{
				pts: [
					[0.05, 0.05],
					[0.35, 0.1],
					[0.2, 0.4],
				],
				ci: 2,
			},
			{
				pts: [
					[0.65, 0.5],
					[0.95, 0.45],
					[0.8, 0.8],
				],
				ci: 0,
			},
			{
				pts: [
					[0.3, 0.75],
					[0.55, 0.85],
					[0.4, 0.95],
				],
				ci: 1,
			},
		],
	},
	pg: {
		bg: '#4f2653',
		colors: ['#6cddd8', '#d86cdd', '#ddd86c'],
		regions: [
			{
				pts: [
					[0.1, 0.15],
					[0.7, 0.2],
					[0.3, 0.75],
				],
				ci: 2,
			},
		],
	},
	pmm: {
		bg: '#331e1d',
		colors: ['#9f47e2', '#e29f47', '#47e29f'],
		regions: [
			{
				pts: [
					[0.3, 0.35],
					[0.45, 0.3],
					[0.42, 0.5],
					[0.28, 0.48],
				],
				ci: 0,
			},
		],
	},
	pmg: {
		bg: '#2e1632',
		colors: ['#6e88d7', '#d76e88', '#88d76e'],
		regions: [
			{
				pts: [
					[0.08, 0.4],
					[0.35, 0.38],
					[0.36, 0.6],
					[0.07, 0.62],
				],
				ci: 1,
			},
			{
				pts: [
					[0.55, 0.1],
					[0.88, 0.08],
					[0.89, 0.3],
					[0.56, 0.32],
				],
				ci: 2,
			},
			{
				pts: [
					[0.3, 0.75],
					[0.65, 0.73],
					[0.67, 0.92],
					[0.32, 0.94],
				],
				ci: 0,
			},
		],
	},
	pgg: {
		bg: '#264241',
		colors: ['#92cf39', '#3992cf', '#cf3992'],
		regions: [
			{
				pts: [
					[0.5, 0.032],
					[0.36, 0.248],
					[0.64, 0.248],
				],
				ci: 2,
			},
			{
				pts: [
					[0.5, 0.968],
					[0.352, 0.848],
					[0.648, 0.848],
				],
				ci: 0,
			},
		],
	},
	cm: {
		bg: '#2a404e',
		colors: ['#63ea86', '#8663ea', '#ea8663'],
		regions: [
			{
				pts: [
					[0.5, 0.25],
					[0.3, 0.65],
					[0.7, 0.65],
				],
				ci: 2,
			},
		],
	},
	cmm: {
		bg: '#273e43',
		colors: ['#8ee054', '#548ee0', '#e0548e'],
		regions: [
			{
				pts: [
					[0.1, 0.1],
					[0.5, 0.05],
					[0.6, 0.4],
					[0.35, 0.5],
					[0.15, 0.3],
				],
				ci: 0,
			},
			{
				pts: [
					[0.5, 0.5],
					[0.85, 0.6],
					[0.9, 0.95],
					[0.55, 0.9],
				],
				ci: 1,
			},
		],
	},
	p4: {
		bg: '#402828',
		colors: ['#d333d8', '#d8d333', '#33d8d3'],
		regions: [
			{
				pts: [
					[0.6, 0.05],
					[0.85, 0.35],
					[0.5, 0.65],
					[0.25, 0.35],
				],
				ci: 1,
			},
		],
	},
	p4m: {
		bg: '#4b2f3f',
		colors: ['#5d58e1', '#e15d58', '#58e15d'],
		regions: [
			{
				pts: [
					[0.08, 0.4],
					[0.35, 0.38],
					[0.36, 0.6],
					[0.07, 0.62],
				],
				ci: 2,
			},
			{
				pts: [
					[0.55, 0.1],
					[0.88, 0.08],
					[0.89, 0.3],
					[0.56, 0.32],
				],
				ci: 0,
			},
			{
				pts: [
					[0.3, 0.75],
					[0.65, 0.73],
					[0.67, 0.92],
					[0.32, 0.94],
				],
				ci: 1,
			},
		],
	},
	p4g: {
		bg: '#4a3f2d',
		colors: ['#c220dd', '#ddc220', '#20ddc2'],
		regions: [
			{
				pts: [
					[0.05, 0.05],
					[0.35, 0.1],
					[0.2, 0.4],
				],
				ci: 2,
			},
			{
				pts: [
					[0.65, 0.5],
					[0.95, 0.45],
					[0.8, 0.8],
				],
				ci: 0,
			},
			{
				pts: [
					[0.3, 0.75],
					[0.55, 0.85],
					[0.4, 0.95],
				],
				ci: 1,
			},
		],
	},
	p3:{"bg":"#522424","colors":["#9251e0","#e09251","#51e092"],"regions":[{"pts":[[0.05,0.1],[0.45,0.1],[0.45,0.35],[0.05,0.35]],"ci":1},{"pts":[[0.55,0.65],[0.95,0.65],[0.95,0.9],[0.55,0.9]],"ci":2}]},
	p3m1: {
		bg: '#1e3c23',
		colors: ['#e58c1b', '#1be58c', '#8c1be5'],
		regions: [
			{
				pts: [
					[0.15, 0.2],
					[0.7, 0.15],
					[0.65, 0.6],
					[0.1, 0.7],
				],
				ci: 1,
			},
		],
	},
	p31m: {
		bg: '#371f1c',
		colors: ['#c06ad7', '#d7c06a', '#6ad7c0'],
		regions: [
			{
				pts: [
					[0.128, 0.424],
					[0.288, 0.352],
					[0.32, 0.628],
					[0.088, 0.628],
				],
				ci: 2,
			},
			{
				pts: [
					[0.784, 0.304],
					[0.896, 0.22],
					[0.848, 0.568],
				],
				ci: 0,
			},
			{
				pts: [
					[0.5, 0.68],
					[0.376, 0.792],
					[0.624, 0.792],
				],
				ci: 1,
			},
		],
	},
	p6: {
		bg: '#2e4f29',
		colors: ['#dc9a48', '#48dc9a', '#9a48dc'],
		regions: [
			{
				pts: [
					[0.08, 0.4],
					[0.35, 0.38],
					[0.36, 0.6],
					[0.07, 0.62],
				],
				ci: 0,
			},
			{
				pts: [
					[0.55, 0.1],
					[0.88, 0.08],
					[0.89, 0.3],
					[0.56, 0.32],
				],
				ci: 1,
			},
			{
				pts: [
					[0.3, 0.75],
					[0.65, 0.73],
					[0.67, 0.92],
					[0.32, 0.94],
				],
				ci: 2,
			},
		],
	},
	p6m: {
		bg: '#352f20',
		colors: ['#df29d2', '#d2df29', '#29d2df'],
		regions: [
			{
				pts: [
					[0.1, 0.1],
					[0.5, 0.05],
					[0.6, 0.4],
					[0.35, 0.5],
					[0.15, 0.3],
				],
				ci: 2,
			},
			{
				pts: [
					[0.5, 0.5],
					[0.85, 0.6],
					[0.9, 0.95],
					[0.55, 0.9],
				],
				ci: 0,
			},
		],
	},
}

// ── Random theme generator ────────────────────────────────────────────────────

function hslHex(h: number, s: number, l: number): string {
	// Convert HSL to hex without external deps
	h = ((h % 360) + 360) % 360
	s /= 100
	l /= 100
	const a = s * Math.min(l, 1 - l)
	const f = (n: number): string => {
		const k = (n + h / 30) % 12
		const color = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
		return Math.round(255 * color)
			.toString(16)
			.padStart(2, '0')
	}
	return `#${f(0)}${f(8)}${f(4)}`
}

/**
 * A set of non-overlapping convex polygon templates in tile-space [0,1]².
 * All coordinates stay within the circle centred at (0.5, 0.5) with radius √0.5 ≈ 0.707.
 */
const REGION_SETS: Array<Array<[number, number][]>> = [
	// 1. Asymmetrical upper-left triangle
	[
		[
			[0.1, 0.15],
			[0.7, 0.2],
			[0.3, 0.75],
		],
	],
	// 2. Two offset rectangles (different sizes, different positions)
	[
		[
			[0.05, 0.1],
			[0.45, 0.1],
			[0.45, 0.35],
			[0.05, 0.35],
		],
		[
			[0.55, 0.65],
			[0.95, 0.65],
			[0.95, 0.9],
			[0.55, 0.9],
		],
	],
	// 3. Rotated off-centre diamond
	[
		[
			[0.6, 0.05],
			[0.85, 0.35],
			[0.5, 0.65],
			[0.25, 0.35],
		],
	],
	// 4. Skewed quadrilateral (not aligned with grid)
	[
		[
			[0.1, 0.2],
			[0.8, 0.15],
			[0.75, 0.8],
			[0.2, 0.7],
		],
	],
	// 5. Three scattered irregular triangles
	[
		[
			[0.05, 0.05],
			[0.35, 0.1],
			[0.2, 0.4],
		],
		[
			[0.65, 0.5],
			[0.95, 0.45],
			[0.8, 0.8],
		],
		[
			[0.3, 0.75],
			[0.55, 0.85],
			[0.4, 0.95],
		],
	],
	// 6. Rotated trapezoid (tilted 45°)
	[
		[
			[0.15, 0.2],
			[0.7, 0.15],
			[0.65, 0.6],
			[0.1, 0.7],
		],
	],
	// 7. Large offset triangle (lower-right bias)
	[
		[
			[0.4, 0.3],
			[0.95, 0.25],
			[0.8, 0.85],
		],
	],
	// 8. Two irregular offset polygons
	[
		[
			[0.1, 0.1],
			[0.5, 0.05],
			[0.6, 0.4],
			[0.35, 0.5],
			[0.15, 0.3],
		],
		[
			[0.5, 0.5],
			[0.85, 0.6],
			[0.9, 0.95],
			[0.55, 0.9],
		],
	],
	// 9. Three scattered offset rectangles
	[
		[
			[0.08, 0.4],
			[0.35, 0.38],
			[0.36, 0.6],
			[0.07, 0.62],
		],
		[
			[0.55, 0.1],
			[0.88, 0.08],
			[0.89, 0.3],
			[0.56, 0.32],
		],
		[
			[0.3, 0.75],
			[0.65, 0.73],
			[0.67, 0.92],
			[0.32, 0.94],
		],
	],
	// 10. Large irregular rotated hexagon
	[
		[
			[0.15, 0.25],
			[0.6, 0.1],
			[0.85, 0.35],
			[0.75, 0.75],
			[0.35, 0.85],
			[0.1, 0.6],
		],
	],
	// 11. Centred triangle
	[
		[
			[0.5, 0.25],
			[0.3, 0.65],
			[0.7, 0.65],
		],
	],
	// 12. Small diamond top-right + small triangle bottom-center
	[
		[
			[0.74, 0.18],
			[0.9, 0.32],
			[0.74, 0.5],
			[0.58, 0.32],
		],
		[
			[0.5, 0.692],
			[0.412, 0.8],
			[0.588, 0.8],
		],
	],
	// 13. Triangle bottom-left + triangle top-right
	[
		[
			[0.1, 0.64],
			[0.02, 0.8],
			[0.38, 0.8],
		],
		[
			[0.752, 0.248],
			[0.832, 0.152],
			[0.648, 0.088],
		],
	],
	// 14. Quadrilateral slightly left of centre
	[
		[
			[0.3, 0.35],
			[0.45, 0.3],
			[0.42, 0.5],
			[0.28, 0.48],
		],
	],
	// 15. Pentagon right side
	[
		[
			[0.7, 0.4],
			[0.78, 0.45],
			[0.75, 0.58],
			[0.62, 0.55],
			[0.65, 0.42],
		],
	],
	// 16. Small triangle at top + small triangle at bottom
	[
		[
			[0.5, 0.032],
			[0.36, 0.248],
			[0.64, 0.248],
		],
		[
			[0.5, 0.968],
			[0.352, 0.848],
			[0.648, 0.848],
		],
	],
	// 17. Pentagon at bottom
	[
		[
			[0.5, 0.7],
			[0.38, 0.75],
			[0.4, 0.6],
			[0.6, 0.6],
			[0.62, 0.75],
		],
	],
	// 18. Quadrilateral left side + triangle right + triangle bottom
	[
		[
			[0.128, 0.424],
			[0.288, 0.352],
			[0.32, 0.628],
			[0.088, 0.628],
		],
		[
			[0.784, 0.304],
			[0.896, 0.22],
			[0.848, 0.568],
		],
		[
			[0.5, 0.68],
			[0.376, 0.792],
			[0.624, 0.792],
		],
	],
	// 19. Triangle top-right + triangle bottom-left
	[
		[
			[0.72, 0.12],
			[0.88, -0.04],
			[0.8, 0.352],
		],
		[
			[0.248, 0.68],
			[0.152, 0.792],
			[0.312, 0.728],
		],
	],
	// 20. Small triangle bottom-right + triangle top-left + triangle top-center
	[
		[
			[0.68, 0.6],
			[0.8, 0.68],
			[0.6, 0.8],
		],
		[
			[0.28, 0.28],
			[0.2, 0.2],
			[0.38, 0.248],
		],
		[
			[0.5, 0.12],
			[0.408, -0.04],
			[0.592, -0.04],
		],
	],
]

/** Generate a random triadic-harmony palette given a starting hue. */
function triadicPalette(
	hue: number,
	saturation = 68,
	lightness = 58,
): [string, string, string] {
	return [
		hslHex(hue, saturation, lightness),
		hslHex(hue + 120, saturation, lightness),
		hslHex(hue + 240, saturation, lightness),
	]
}

export function randomTheme(rng: () => number = Math.random): WpgTheme {
	const hue = rng() * 360
	// Vary saturation/lightness a little
	const sat = 55 + rng() * 25
	const lit = 48 + rng() * 20
	const colors = triadicPalette(hue, sat, lit)
	const bg = hslHex(hue + 60 + rng() * 60, 20 + rng() * 20, 14 + rng() * 10)
	const regionSet = REGION_SETS[Math.floor(rng() * REGION_SETS.length)]!
	// Randomly permute which color index each region gets
	const startCi = Math.floor(rng() * 3)
	const regions = regionSet.map((pts, i) => ({
		pts,
		ci: ((startCi + i) % 3) as 0 | 1 | 2,
	}))
	return { bg, colors, regions }
}

/** Generate N distinct random themes using a seeded-ish sequence. */
export function randomThemes(n: number, seed?: number): WpgTheme[] {
	// Simple LCG for reproducibility when seed is given
	let s = seed ?? Date.now()
	const rng = (): number => {
		s = (s * 1664525 + 1013904223) & 0xffffffff
		return (s >>> 0) / 0x100000000
	}
	return Array.from({ length: n }, () => randomTheme(rng))
}
